import { useDialogs } from './ui'
import { useEditor, EditorContent, Node } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { FontFamily } from '@tiptap/extension-font-family'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { colors, fontSize, radius, spacing } from '../styles/theme'

// A direct-URL video node (mp4/webm). Images use the Image extension; external embeds can use a link.
const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({ src: { default: null } }),
  parseHTML: () => [{ tag: 'video' }],
  renderHTML: ({ HTMLAttributes }) => ['video', { controls: 'true', ...HTMLAttributes }],
})

const FONTS = [
  { label: 'Default', value: '' },
  { label: 'Sans', value: 'system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Mono', value: 'ui-monospace, monospace' },
]

function Btn({ onClick, active, children, title }: { onClick: () => void; active?: boolean; children: React.ReactNode; title: string }) {
  return (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick() }}
      style={{
        minWidth: 28, height: 28, padding: '0 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
        background: active ? colors.primaryLight : colors.surface, color: active ? colors.primary : colors.text,
        cursor: 'pointer', fontSize: fontSize.sm, fontWeight: active ? 700 : 400,
      }}>{children}</button>
  )
}

/** Rich-text editor (Tiptap) producing sanitized-on-render HTML: formatting, fonts, colour, align, links, images, video. */
export function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const dialogs = useDialogs()
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false } as never),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      Link.configure({ openOnClick: false, autolink: true }),
      Video,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  if (!editor) return null

  const promptInsert = async (label: string, run: (url: string) => void) => {
    const url = await dialogs.prompt({ title: `Insert ${label.split(' ')[0].toLowerCase()}`, label, placeholder: 'https://', confirmLabel: 'Insert', required: true })
    if (url?.trim()) run(url.trim())
  }

  return (
    <>
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6, borderBottom: `1px solid ${colors.border}` }}>
        <Btn title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></Btn>
        <Btn title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></Btn>
        <Btn title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></Btn>
        <Btn title="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H</Btn>
        <Btn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</Btn>
        <Btn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</Btn>
        <Btn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>⯇</Btn>
        <Btn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>≡</Btn>
        <Btn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>⯈</Btn>
        <select title="Font" onChange={e => editor.chain().focus().setFontFamily(e.target.value).run()}
          style={{ height: 28, border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm }}>
          {FONTS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <input type="color" title="Text colour" onChange={e => editor.chain().focus().setColor(e.target.value).run()}
          style={{ width: 28, height: 28, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: 0, cursor: 'pointer' }} />
        <Btn title="Link" active={editor.isActive('link')} onClick={() => promptInsert('Link URL', url => editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run())}>🔗</Btn>
        <Btn title="Image" onClick={() => promptInsert('Image URL', url => editor.chain().focus().setImage({ src: url }).run())}>🖼</Btn>
        <Btn title="Video" onClick={() => promptInsert('Video URL (mp4/webm)', url => editor.chain().focus().insertContent({ type: 'video', attrs: { src: url } }).run())}>▶</Btn>
      </div>
      <EditorContent editor={editor}
        style={{ padding: spacing.md, minHeight: 140, fontSize: fontSize.sm, color: colors.text }} />
    </div>
      {dialogs.element}
    </>
  )
}
